import React, { useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  Background,
  BackgroundVariant,
  Node,
  Edge,
  MarkerType,
} from '@xyflow/react';
import { Card, CardContent, Typography, Chip, Box, Divider, useTheme } from '@mui/material';
import { EquipmentNode, EquipData, SideHandles } from './EquipmentShapes';
import { NodeProps } from '@xyflow/react';
import { PulpPaperRoute } from '../../types';
import { installPidLint } from './pidLint';
import { installPidHops } from './pidHop';
import { measureLaneTargets } from './pidAlign';

/**
 * 硫酸盐法（KP）制浆 + 碱回收 P&ID 风格详细流程图
 * 蒸煮 → 洗涤 → 氧脱木素 → 三段漂白（D0-EOP-D1）主线
 * 黑液蒸发浓缩 → 碱回收炉燃烧 → 绿液苛化 → 白液回蒸煮的碱闭环
 * 白泥 → 石灰窑煅烧 → 石灰回苛化的钙闭环
 * 设计遵循《General information/流程图详细化设计准则.md》
 */

export type Medium = 'pulp' | 'black' | 'alkali' | 'lime' | 'chem' | 'water' | 'steam' | 'fuel';

const MEDIUM_META: Record<
  Medium,
  { label: string; light: string; dark: string; width: number; dash?: string }
> = {
  pulp: { label: '木片 / 纸浆', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  black: { label: '黑液', light: '#6d4c41', dark: '#a1887f', width: 2.6 },
  alkali: { label: '绿液 / 白液（碱循环）', light: '#558b2f', dark: '#9ccc65', width: 2.6 },
  lime: { label: '石灰 / 白泥（固相循环）', light: '#757575', dark: '#b0bec5', width: 3.2, dash: '12 5' },
  chem: { label: 'O₂ / ClO₂ / H₂O₂', light: '#00838f', dark: '#4dd0e1', width: 2 },
  water: { label: '水', light: '#1565c0', dark: '#64b5f6', width: 2 },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
  fuel: { label: '燃料', light: '#c2185b', dark: '#f06292', width: 2, dash: '7 4' },
};

interface PortData extends Record<string, unknown> {
  label: string;
  color: string;
  direction: 'in' | 'out';
}

const PortNode: React.FC<NodeProps> = ({ data }) => {
  const d = data as PortData;
  return (
    <div
      className="pid-label"
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: d.color,
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        position: 'relative',
      }}
    >
      {d.direction === 'in' ? '▶' : '◀'} {d.label}
      <SideHandles />
    </div>
  );
};

const nodeTypes = { equip: EquipmentNode, port: PortNode };

interface Dev {
  id: string;
  kind: EquipData['kind'];
  x: number;
  y: number;
  tag: string;
  name: string;
  note?: string;
}

const DEVICES: Dev[] = [
  // 主线设备 handle 中线全部对齐 y=243（浆料主线一条贯通直线，无弯折）
  { id: 'dc101', kind: 'hDrum', x: 72, y: 215, tag: 'DC-101', name: '木片仓' },
  { id: 'd201', kind: 'digester', x: 230, y: 148, tag: 'D-201', name: '蒸煮器', note: '160~170℃' },
  { id: 'd202', kind: 'blowTank', x: 330, y: 168, tag: 'D-202', name: '喷放锅' },
  { id: 'w301', kind: 'washer', x: 470, y: 214, tag: 'W-301', name: '洗浆机', note: '黑液提取' },
  { id: 'o401', kind: 'tower', x: 600, y: 158, tag: 'O-401', name: '氧脱木素塔', note: 'O₂ 0.4MPa' },
  { id: 'w402', kind: 'washer', x: 730, y: 214, tag: 'W-402', name: '洗浆机' },
  { id: 'b501', kind: 'tower', x: 860, y: 158, tag: 'B-501', name: 'D0 漂白塔', note: 'ClO₂ 60℃' },
  { id: 'b502', kind: 'tower', x: 1120, y: 158, tag: 'B-502', name: 'EOP 塔', note: 'H₂O₂+O₂ 80℃' },
  { id: 'b503', kind: 'tower', x: 1400, y: 158, tag: 'B-503', name: 'D1 漂白塔', note: 'ClO₂ 70℃' },
  // 蒸发/碱回收链 handle 中线对齐 y=449
  { id: 'e601', kind: 'exchanger', x: 560, y: 420, tag: 'E-601', name: '蒸发器 Ⅰ效' },
  { id: 'e602', kind: 'exchanger', x: 680, y: 420, tag: 'E-602', name: '蒸发器 Ⅱ效' },
  { id: 'v610', kind: 'vDrum', x: 800, y: 393, tag: 'V-610', name: '浓黑液罐', note: '固形物 65%' },
  { id: 'f701', kind: 'furnace', x: 980, y: 385, tag: 'F-701', name: '碱回收炉', note: '炉膛 ~950℃' },
  { id: 'd702', kind: 'vDrum', x: 1130, y: 393, tag: 'D-702', name: '绿液溶解槽' },
  { id: 'c703', kind: 'clarifier', x: 1250, y: 414, tag: 'C-703', name: '绿液澄清器' },
  { id: 'k801', kind: 'hDrum', x: 1400, y: 421, tag: 'K-801', name: '苛化器', note: '石灰乳苛化' },
  { id: 'c802', kind: 'clarifier', x: 1580, y: 414, tag: 'C-802', name: '白液澄清器' },
  { id: 'l901', kind: 'limeKiln', x: 1750, y: 660, tag: 'L-901', name: '石灰窑', note: '白泥煅烧' },
  // 走线汇流点（无标注）：强制白液回用线走底层车道 y≈620，x 对准蒸煮器底部管口
  { id: 'j201a', kind: 'mixer', x: 1565, y: 612, tag: '', name: '' },
  { id: 'j201b', kind: 'mixer', x: 215, y: 612, tag: '', name: '' },
];

interface Port {
  id: string;
  label: string;
  medium: Medium;
  x: number;
  y: number;
  direction: 'in' | 'out';
}

const PORTS: Port[] = [
  { id: 'p-wood', label: '木片', medium: 'pulp', x: 0, y: 234, direction: 'in' },
  { id: 'p-pulp', label: '漂白浆（抄纸）', medium: 'pulp', x: 1550, y: 234, direction: 'out' },
  { id: 'p-w1', label: '洗涤清水', medium: 'water', x: 430, y: 120, direction: 'in' },
  { id: 'p-w2', label: '洗涤清水', medium: 'water', x: 600, y: 120, direction: 'in' },
  { id: 'p-chem', label: 'O₂ / ClO₂ / H₂O₂', medium: 'chem', x: 1100, y: 40, direction: 'in' },
  { id: 'p-stm', label: '蒸汽', medium: 'steam', x: 480, y: 540, direction: 'in' },
  { id: 'p-stmout', label: '副产蒸汽', medium: 'steam', x: 955, y: 60, direction: 'out' },
  { id: 'p-fuel', label: '窑用燃料', medium: 'fuel', x: 1930, y: 679, direction: 'in' },
];

interface Pipe {
  id: string;
  from: string;
  fromSide: 'top' | 'bottom' | 'left' | 'right';
  to: string;
  toSide: 'top' | 'bottom' | 'left' | 'right';
  medium: Medium;
  label?: string;
}

const PIPES: Pipe[] = [
  // ── 浆料主线 ──
  { id: 'k01', from: 'p-wood', fromSide: 'right', to: 'dc101', toSide: 'left', medium: 'pulp' },
  { id: 'k02', from: 'dc101', fromSide: 'right', to: 'd201', toSide: 'left', medium: 'pulp' },
  { id: 'k03', from: 'd201', fromSide: 'right', to: 'd202', toSide: 'left', medium: 'pulp' },
  { id: 'k04', from: 'd202', fromSide: 'right', to: 'w301', toSide: 'left', medium: 'pulp' },
  { id: 'k05', from: 'w301', fromSide: 'right', to: 'o401', toSide: 'left', medium: 'pulp' },
  { id: 'k06', from: 'o401', fromSide: 'right', to: 'w402', toSide: 'left', medium: 'pulp' },
  { id: 'k07', from: 'w402', fromSide: 'right', to: 'b501', toSide: 'left', medium: 'pulp', label: '未漂浆' },
  { id: 'k08', from: 'b501', fromSide: 'right', to: 'b502', toSide: 'left', medium: 'pulp' },
  { id: 'k09', from: 'b502', fromSide: 'right', to: 'b503', toSide: 'left', medium: 'pulp' },
  { id: 'k10', from: 'b503', fromSide: 'right', to: 'p-pulp', toSide: 'left', medium: 'pulp', label: '漂白浆' },
  // ── 黑液蒸发浓缩 ──
  { id: 'k11', from: 'w301', fromSide: 'bottom', to: 'e601', toSide: 'top', medium: 'black', label: '稀黑液' },
  { id: 'k12', from: 'w402', fromSide: 'bottom', to: 'e602', toSide: 'top', medium: 'black' },
  { id: 'k13', from: 'e601', fromSide: 'right', to: 'e602', toSide: 'left', medium: 'black' },
  { id: 'k14', from: 'e602', fromSide: 'right', to: 'v610', toSide: 'left', medium: 'black' },
  { id: 'k15', from: 'v610', fromSide: 'right', to: 'f701', toSide: 'left', medium: 'black', label: '浓黑液 65%' },
  // ── 燃烧 + 苛化（碱循环）──
  { id: 'k16', from: 'f701', fromSide: 'right', to: 'd702', toSide: 'left', medium: 'alkali' },
  { id: 'k17', from: 'd702', fromSide: 'right', to: 'c703', toSide: 'left', medium: 'alkali' },
  { id: 'k18', from: 'c703', fromSide: 'right', to: 'k801', toSide: 'left', medium: 'alkali', label: '绿液' },
  { id: 'k19', from: 'k801', fromSide: 'right', to: 'c802', toSide: 'left', medium: 'alkali', label: '白液' },
  // 白液回用（重组分走底层车道 y≈620）：c802 底 → 汇流 J-A → 底部横干道 → 汇流 J-B → 蒸煮器底
  { id: 'k20a', from: 'c802', fromSide: 'bottom', to: 'j201a', toSide: 'top', medium: 'alkali' },
  { id: 'k20b', from: 'j201a', fromSide: 'left', to: 'j201b', toSide: 'right', medium: 'alkali', label: '白液回用' },
  { id: 'k20c', from: 'j201b', fromSide: 'top', to: 'd201', toSide: 'bottom', medium: 'alkali' },
  // ── 钙循环（白泥 → 石灰 → 苛化）：绕 C-802 标签区外侧 ──
  { id: 'k21', from: 'c802', fromSide: 'right', to: 'l901', toSide: 'top', medium: 'lime' },
  { id: 'k22', from: 'l901', fromSide: 'top', to: 'k801', toSide: 'bottom', medium: 'lime', label: '石灰回用' },
  // ── 公用工程 ──
  { id: 'k23', from: 'p-w1', fromSide: 'right', to: 'w301', toSide: 'top', medium: 'water' },
  { id: 'k24', from: 'p-w2', fromSide: 'right', to: 'w402', toSide: 'top', medium: 'water' },
  { id: 'k25', from: 'p-chem', fromSide: 'left', to: 'o401', toSide: 'left', medium: 'chem', label: 'O₂' },
  { id: 'k26', from: 'p-chem', fromSide: 'left', to: 'b501', toSide: 'top', medium: 'chem', label: 'ClO₂' },
  { id: 'k27', from: 'p-chem', fromSide: 'bottom', to: 'b502', toSide: 'top', medium: 'chem', label: 'H₂O₂' },
  { id: 'k28', from: 'p-chem', fromSide: 'right', to: 'b503', toSide: 'top', medium: 'chem', label: 'ClO₂' },
  { id: 'k29', from: 'p-stm', fromSide: 'right', to: 'e601', toSide: 'bottom', medium: 'steam' },
  { id: 'k30', from: 'f701', fromSide: 'top', to: 'p-stmout', toSide: 'bottom', medium: 'steam' },
  { id: 'k31', from: 'p-fuel', fromSide: 'left', to: 'l901', toSide: 'right', medium: 'fuel' },
];

interface FlowDiagramProps {
  route: PulpPaperRoute;
}

const KPInner: React.FC<FlowDiagramProps> = ({ route }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const rfInstance = useReactFlow();
  // 车道吸附：目标 y 写回数据源（受控模式下 setNodes 会被 props 回滚）
  const [snappedY, setSnappedY] = useState<Record<string, number>>({});

  useEffect(() => {
    installPidLint();
    installPidHops();
  }, []);

  useEffect(() => {
    const apply = () => {
      try {
        const t = measureLaneTargets(rfInstance);
        if (Object.keys(t).length > 0) setSnappedY((prev) => ({ ...prev, ...t }));
      } catch {
        /* 单次失败由后续调度补 */
      }
    };
    const t1 = setTimeout(apply, 120);
    const t2 = setTimeout(apply, 900);
    const doc = document as Document & { fonts?: { ready: Promise<unknown> } };
    let t3: ReturnType<typeof setTimeout> | undefined;
    doc.fonts?.ready.then(() => {
      t3 = setTimeout(apply, 60);
    });
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (t3) clearTimeout(t3);
    };
  }, [rfInstance]);

  const { nodes, edges } = useMemo(() => {
    const ns: Node[] = [
      ...DEVICES.map<Node>((d) => ({
        id: d.id,
        type: 'equip',
        position: { x: d.x, y: snappedY[d.id] ?? d.y },
        data: { kind: d.kind, tag: d.tag, name: d.name, note: d.note, dark: isDark } as EquipData,
        draggable: true,
      })),
      ...PORTS.map<Node>((p) => ({
        id: p.id,
        type: 'port',
        position: { x: p.x, y: snappedY[p.id] ?? p.y },
        data: {
          label: p.label,
          color: MEDIUM_META[p.medium][isDark ? 'dark' : 'light'],
          direction: p.direction,
        } as PortData,
        draggable: true,
      })),
    ];
    const es: Edge[] = PIPES.map((p) => {
      const m = MEDIUM_META[p.medium];
      const color = m[isDark ? 'dark' : 'light'];
      return {
        id: p.id,
        source: p.from,
        sourceHandle: `${p.fromSide}-s`,
        target: p.to,
        targetHandle: `${p.toSide}-t`,
        type: 'smoothstep' as const,
        animated: p.medium === 'lime',
        label: p.label,
        labelStyle: { fill: isDark ? '#eceff1' : '#37474f', fontSize: 10, fontWeight: 600 },
        labelBgStyle: { fill: isDark ? '#1e1e1e' : '#ffffff' },
        style: { stroke: color, strokeWidth: m.width, strokeDasharray: m.dash },
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
      };
    });
    return { nodes: ns, edges: es };
  }, [isDark, snappedY]);

  return (
    <Card sx={{ backgroundColor: isDark ? '#1e1e1e' : '#ffffff' }}>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ color: isDark ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}>
          {route.name} · 硫酸盐法（KP）制浆与碱回收详细流程
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          蒸煮 → 洗涤 → 氧脱木素 → 三段漂白（D0-EOP-D1）主线；黑液蒸发 → 碱回收炉燃烧 → 绿液苛化 → 白液回蒸煮的碱闭环；
          白泥经石灰窑煅烧回苛化的钙闭环（设备形状按 P&ID 惯例绘制，管线按介质分色，可拖动 / 缩放查看细节）
        </Typography>
        <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">原料</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {route.feedstock.map((item) => (
                <Chip key={item} label={item} size="small" color="primary" variant="outlined" />
              ))}
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">产品 / 副产</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {[...(route.products ?? []), ...(route.byproducts ?? [])].map((item) => (
                <Chip key={item} label={item} size="small" color="success" variant="outlined" />
              ))}
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: 1.5, borderColor: isDark ? '#444444' : '#e0e0e0' }} />
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          {(Object.keys(MEDIUM_META) as Medium[]).map((k) => (
            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 26,
                  height: 0,
                  borderTopWidth: MEDIUM_META[k].dash ? 2 : 3,
                  borderTopStyle: MEDIUM_META[k].dash ? 'dashed' : 'solid',
                  borderTopColor: MEDIUM_META[k][isDark ? 'dark' : 'light'],
                }}
              />
              <Typography variant="caption" sx={{ fontSize: 11 }}>
                {MEDIUM_META[k].label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            height: 640,
            border: '1px solid',
            borderColor: isDark ? '#444444' : '#e0e0e0',
            borderRadius: 2,
          }}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.08, maxZoom: 0.6 }}
            minZoom={0.05}
            maxZoom={2}
            zoomOnDoubleClick={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={16}
              size={1}
              color={isDark ? '#333333' : '#e0e0e0'}
            />
          </ReactFlow>
        </Box>

        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {[
            '锥底立塔 = 蒸煮器',
            '矮胖锥底罐 = 喷放锅',
            '竖长圆顶塔 = 漂白 / 氧反应塔',
            '圆鼓半浸浆槽 = 洗浆机',
            '宽扁罐带耙臂 = 澄清器',
            '横长圆筒 + 支撑轮 = 石灰窑',
            '方箱 + 烟囱 = 碱回收炉',
            '圆形 = 蒸发器',
          ].map((t) => (
            <Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: 10 }} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const KPDiagram: React.FC<FlowDiagramProps> = (props) => (
  <ReactFlowProvider>
    <KPInner {...props} />
  </ReactFlowProvider>
);

export default KPDiagram;
